import * as React from 'react';
import { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { RedirectComputedPath } from '../pages/components/RedirectComputedPath';
import { ProtectedRoute } from './ProtectedRoute';

// Lazy-loaded page components
const Homepage = React.lazy(() => import('./../pages/homepage/Homepage'));
const SearchResultsPerson = React.lazy(() => import('./../pages/dashboard/SearchResultsPerson'));
const SearchResultsOrganisation = React.lazy(() => import('./../pages/dashboard/SearchResultsOrganisation'));
const SearchResultsWork = React.lazy(() => import('./../pages/dashboard/SearchResultsWork'));
const SearchResultsProject = React.lazy(() => import('./../pages/dashboard/SearchResultsProject'));
const DetailWork = React.lazy(() => import('./../pages/detail/DetailWork'));
const DetailOrganisation = React.lazy(() => import('./../pages/detail/DetailOrganisation'));
const DetailPerson = React.lazy(() => import('./../pages/detail/DetailPerson'));
const DetailProject = React.lazy(() => import('./../pages/detail/DetailProject'));
const EditWork = React.lazy(() => import('./../pages/edit/work/EditWork'));
const EditOrganisation = React.lazy(() => import('./../pages/edit/organisation/EditOrganisation'));
const EditPerson = React.lazy(() => import('./../pages/edit/person/EditPerson'));
const EditProject = React.lazy(() => import('./../pages/edit/project/EditProject'));
const PublicationListEditor = React.lazy(() => import('./../pages/publig/PublicationListEditor'));
const PubListDoc = React.lazy(() => import('./../pages/publigDocumentation/PublicDocumentation'));
const AdminAreaUser = React.lazy(() => import('../pages/admin/adminAreaUser/AdminAreaUser'));
const AdminAreaSystem = React.lazy(() => import('../pages/admin/adminAreaSystem/AdminAreaSystem'));
const AdminMathjaxTest = React.lazy(() => import('../pages/admin/mathjaxTest/AdminMathjaxTest'));
const Settings = React.lazy(() => import('./../pages/settings/Settings'));
const Profile = React.lazy(() => import('./../pages/profile/Profile'));
const Error = React.lazy(() => import('./../pages/system/ErrorPage'));
const Import = React.lazy(() => import('./../pages/import/Import'));
const Upload = React.lazy(() => import('./../pages/upload/Upload'));
const Logout = React.lazy(() => import('./../pages/Logout'));
const Login = React.lazy(() => import('./../pages/Login'));

interface Props {
}

export const Routing = (props: Props): ReactNode => {

  return (
    <Routes>
      <Route path="/" element={<Homepage />} />
      <Route path="/search">
        <Route index element={<Navigate to={'/search/works'} replace={true} />} />
        <Route path="works" element={<SearchResultsWork />} />
        <Route path="persons" element={<SearchResultsPerson />} />
        <Route path="organisations" element={<SearchResultsOrganisation />} />
        <Route path="projects" element={<SearchResultsProject />} />
      </Route>
      <Route path={'/work'}>
        <Route path=":id" element={<DetailWork />} />
        <Route
          path="add/:page?"
          element={
            <ProtectedRoute>
              <EditWork />
            </ProtectedRoute>
          }
        />
        <Route
          path=":id/edit/:page?"
          element={
            <ProtectedRoute>
              <EditWork />
            </ProtectedRoute>
          }
        />
        <Route
          path="import"
          element={
            <ProtectedRoute>
              <Import />
            </ProtectedRoute>
          }
        />
        <Route
          path="upload"
          element={
            <ProtectedRoute>
              <Upload />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path={'/person'}>
        <Route path=":id" element={<DetailPerson />} />
        <Route
          path="add"
          element={
            <ProtectedRoute>
              <EditPerson />
            </ProtectedRoute>
          }
        />
        <Route
          path=":id/edit"
          element={
            <ProtectedRoute>
              <EditPerson />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path={'/organisation'}>
        <Route path=":id" element={<DetailOrganisation />} />
        <Route
          path="add"
          element={
            <ProtectedRoute>
              <EditOrganisation />
            </ProtectedRoute>
          }
        />
        <Route
          path=":id/edit"
          element={
            <ProtectedRoute>
              <EditOrganisation />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path={'/project'}>
        <Route path=":id" element={<DetailProject />} />
        <Route
          path="add"
          element={
            <ProtectedRoute>
              <EditProject />
            </ProtectedRoute>
          }
        />
        <Route
          path=":id/edit"
          element={
            <ProtectedRoute>
              <EditProject />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path={'/publist'}>
        <Route index element={<Navigate to={'/publist/generator/publications'} replace={true} />} />
        <Route path="documentation" element={<PubListDoc />} />
        <Route path={'generator'}>
          <Route index element={<Navigate to={'/publist/generator/publications'} replace={true} />} />
          <Route path=":type/:apikey?" element={<PublicationListEditor />} />
        </Route>
      </Route>
      <Route path={'/admin'}>
        <Route
          path="users"
          element={
            <ProtectedRoute roles={['ROLE_ADMIN']}>
              <AdminAreaUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="system"
          element={
            <ProtectedRoute roles={['ROLE_ADMIN', 'ROLE_EDITOR']}>
              <AdminAreaSystem />
            </ProtectedRoute>
          }
        />
        <Route
          path="mathjax-test"
          element={
            <ProtectedRoute roles={['ROLE_ADMIN']}>
              <AdminMathjaxTest />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path={'/user'}>
        <Route index element={<Navigate to={'/user/profile'} replace={true} />} />
        <Route
          path="profile/:tab?"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/:tab?"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Authentication */}
      <Route
        path="/oauth"
        element={
          <RedirectComputedPath
            computePathFunc={(): string => {
              const target_path: string = sessionStorage.getItem('target_path');
              if (target_path) sessionStorage.removeItem('target_path');
              return target_path ?? '/';
            }}
          />
        }
      />
      <Route path="/logout" element={<Logout />} />
      <Route path="/login" element={<Login />} />

      {/* Error Pages */}
      <Route path="/error/:code?" element={<Error />} />
      <Route path={'*'} element={<Navigate to="/error/404" replace={true} />} />
    </Routes>
  );
};
